import React, { useContext } from 'react';
import GeneralContext from '../../GeneralContext';
import { Select, NumberInput } from './RemindersStyledInputs';
import ActionCardEngine from '../../../components/engine/ToggledActionsCardEngine';
import { TextString } from '../../../components/Text';
import Title from '../../EditableTitle';
import useTranslations from '../../../hooks/useTranslations';
import AdditionalSettingsGrid from '../engine/AdditionalReminderCardSettingsEngine';
import Disabler from '../../../components/Disabler';
import RecurringEngine from '../engine/RecurringEngine';
import SchedularSettings from '../engine/SchedularSettingsQueryEngine';
import Animation from '../../../components/Animation';
import ConditionsQueryBuilderEngine from '../../../components/conditions-query-builder/engine/ConditionsQueryBuilderEngine';
import { Options } from '../../../components/conditions-query-builder/view/ConditionsQueryBuilderView';

const ReminderCardDateView = props => {
  const api = useContext(GeneralContext);
  const { reminderData, update, logicRules } = props;
  const { form } = api;
  const t = useTranslations();

  return (
    <ActionCardEngine
      collapse
      expandViaArrow={!reminderData.isActive}
      id={reminderData.id}
      actionButtons={props.actionButtons}
      onToggleChange={() =>
        update(
          { isActive: !reminderData.isActive },
          t('snackbars.reminders.toggle', {
            isActive: !reminderData.isActive ? 'on' : 'off'
          })
        )
      }
      onDelete={() => api.deleteReminder(reminderData.id)}
      isActive={reminderData.isActive}
      disabled={reminderData.isActive}
      neverDisableActionButtons
      onDisabledClicked={props.onDisabledClicked}
      initAsExpanded={props.initAsExpanded}
      collapseSummary={
        <Disabler
          disabled={reminderData.isActive}
          onDisabledClicked={props.onDisabledClicked}
        >
          <Title
            alignText
            id={reminderData.id}
            placeholder={t('form-builder.reminders.name.placeholder')}
            content={props.title}
            setContent={props.setTitle}
            onBlur={props.updateTitle}
            inputProps={{ maxLength: 80 }}
          />
        </Disabler>
      }
      deleteDialogProps={{
        content: 'form-builder.reminders.delete_dialog.body'
      }}
    >
      <SchedularSettings {...{ reminderData, update }} />
      <TextString content="form-builder.reminders.type_2_to_review_a_form" />
      <Select
        data-testid={'status-2-' + reminderData.id}
        onChange={props.onStatusDDChange}
        value={
          reminderData.condition.statusList &&
          !!reminderData.condition.statusList.length
            ? reminderData.condition.statusList
            : ['all', ...props.statusesDD.map(s => s.value)]
        }
        variant="inline"
        allowMultiSelect
        dontAllowEmptyMuliSelect
        aggregateMultiSelectDisplay
        options={props.statusesDD}
      />

      {reminderData.condition.dateOperator !== 'on' && (
        <>
          <NumberInput
            data-testid={'interval-1-' + reminderData.id}
            onChange={e => {
              let newValue = parseInt(e.target.value);
              // interval number can't be lower than 1
              if (newValue < 1) {
                newValue = 1;
              }
              props.setIntervalCount(newValue);
            }}
            onBlur={() => update({ 'condition.interval': props.intervalCount })}
            type="number"
            value={props.intervalCount}
          />
          <Select
            data-testid={'interval-unit-2-' + reminderData.id}
            onChange={value => update({ 'condition.intervalUnit': value })}
            value={reminderData.condition.intervalUnit}
            variant="inline"
            options={[
              {
                value: 'day',
                text: t('form-builder.reminders.days', {
                  count: reminderData.condition.interval
                }),
                'data-testid': 'interval-unit-2-' + reminderData.id + '-day'
              },
              {
                value: 'week',
                text: t('form-builder.reminders.weeks', {
                  count: reminderData.condition.interval
                }),
                'data-testid': 'interval-unit-2-' + reminderData.id + '-week'
              },
              {
                value: 'month',
                text: t('form-builder.reminders.months', {
                  count: reminderData.condition.interval
                }),
                'data-testid': 'interval-unit-2-' + reminderData.id + '-month'
              }
            ]}
          />
        </>
      )}
      <Select
        data-testid={'when-unit-2-' + reminderData.id}
        onChange={newValue => {
          const updateValue = { 'condition.dateOperator': newValue };
          if (newValue !== 'after') {
            // we know allDateFields[1] is populated for sure, otherwise
            // we coudn't choose any option that isn't 'after'
            Object.assign(updateValue, {
              'condition.dateFieldName': api.allDateFields[1].internalId
            });
          }
          update(updateValue);
        }}
        variant="inline"
        value={reminderData.condition.dateOperator}
        options={[
          ...(api.allDateFields.length > 1
            ? [
                {
                  value: 'on',
                  text: 'form-builder.reminders.on',
                  'data-testid': 'date-operator-2-' + reminderData.id + '-on'
                },
                {
                  value: 'before',
                  text: 'form-builder.reminders.before',
                  'data-testid':
                    'date-operator-2-' + reminderData.id + '-before'
                }
              ]
            : []),
          {
            value: 'after',
            text: 'form-builder.reminders.after',
            'data-testid': 'date-operator-2-' + reminderData.id + '-after'
          }
        ]}
      />

      <Select
        data-testid={'field-2-' + reminderData.id}
        variant="inline"
        onChange={newValue => {
          const updateValue = { 'condition.dateFieldName': newValue };
          if (newValue === 'updatedDate') {
            Object.assign(updateValue, {
              'condition.dateOperator': 'after'
            });
          }
          update(updateValue);
        }}
        // onOpen={
        //   /* in the future we may want to see the option in bold only when the menu is open*/
        // }
        // onClose={
        //   /* in the future we may want to see the option in bold only when the menu is open*/
        // }
        value={
          reminderData.condition.dateFieldName ||
          api.allDateFields[0].internalId
        }
        searchable={api.allDateFields.length}
        options={api.allDateFields.map(option => ({
          value: option.internalId,
          text: option.title,
          'data-testid': 'field-2' + reminderData.id + '-' + option.title
        }))}
      />
      <ConditionsQueryBuilderEngine
        form={form}
        backEndConditions={logicRules}
        update={conditions => update({ 'condition.logicRules': conditions })}
        variant="inline"
        condtionsPrefixText={t('logic.and')}
        useSpacers
        filter={[Options.DATE_RANGE]}
      />

      <AdditionalSettingsGrid {...{ reminderData, update }} />
      <Animation
        in={!props.hideRecurring}
        type="collapse"
        mountOnEnter
        unmountOnExit
      >
        <RecurringEngine reminderData={reminderData} update={update} />
      </Animation>
    </ActionCardEngine>
  );
};

export default ReminderCardDateView;
